import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Table, Button, Modal, Input, Form, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EnvironmentOutlined } from '@ant-design/icons';
import BASEURL from '../API/Config';

const PincodeMaster = () => {
  const [pincodes, setPincodes] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');

  const fetchPincodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASEURL}/master/getpin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPincodes(data);
      setFiltered(data);
    } catch {
      Swal.fire('Error', 'Failed to load pincodes.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPincodes(); }, []);

  const handleSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setSearch(q);
    setFiltered(pincodes.filter(p => (p.name?.[0] || '').includes(q)));
  };

  const openModal = (pincode = null) => {
    if (pincode) {
      setIsEditing(true);
      setEditId(pincode._id);
      form.setFieldsValue({ pincode: pincode.name?.[0] || '' });
    } else {
      setIsEditing(false);
      setEditId(null);
      form.resetFields();
    }
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    try {
      const url = isEditing
        ? `${BASEURL}/master/updatepincode/${editId}`
        : `${BASEURL}/master/createpin`;
      const method = isEditing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: [values.pincode] }),
      });
      if (!res.ok) throw new Error(await res.text());
      Swal.fire('Success', isEditing ? 'Pincode updated!' : 'Pincode created!', 'success');
      setShowModal(false);
      fetchPincodes();
    } catch (err) {
      Swal.fire('Error', err.message || 'Failed.', 'error');
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Pincode?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53935',
      confirmButtonText: 'Yes, Delete',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await fetch(`${BASEURL}/master/deletepincode/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire('Deleted!', 'Pincode removed.', 'success');
        fetchPincodes();
      }
    });
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_, __, i) => <span style={{ color: '#6b7280', fontWeight: 500 }}>{i + 1}</span>,
    },
    {
      title: 'PINCODE',
      key: 'pincode',
      render: (r) => (
        <Space>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#e8f1ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2874f0', fontSize: 14 }}>
            <EnvironmentOutlined />
          </div>
          <span style={{ fontWeight: 600, color: '#212121' }}>{r.name?.[0]}</span>
        </Space>
      ),
    },
    {
      title: 'STATUS',
      key: 'status',
      render: () => <Tag color="green" style={{ borderRadius: 50, fontWeight: 600 }}>Active</Tag>,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 120,
      render: (r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}
            style={{ color: '#2874f0', borderColor: '#2874f0', borderRadius: 4 }} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(r._id)}
            style={{ borderRadius: 4 }} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>Masters</div>
          <h2 style={{ margin: 0, fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 700, color: '#212121' }}>Pincode Master</h2>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}
          style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, height: 38, fontWeight: 600 }}>
          Add Pincode
        </Button>
      </div>

      {/* Table Card */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e8ecf0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        {/* Toolbar */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />} placeholder="Search pincode..."
            value={search} onChange={handleSearch}
            style={{ width: 240, borderRadius: 4, borderColor: '#e0e0e0' }} />
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#6b7280' }}>
            Total: <strong style={{ color: '#212121' }}>{filtered.length}</strong>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10, showTotal: (t) => `${t} pincodes`, style: { padding: '12px 20px' } }}
          size="middle"
        />
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={<span style={{ fontFamily: 'Outfit,sans-serif', fontWeight: 700 }}>{isEditing ? 'Edit Pincode' : 'Add Pincode'}</span>}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        centered
        width={420}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item name="pincode" label="Pincode (6 digits)"
            rules={[
              { required: true, message: 'Pincode is required' },
              { pattern: /^\d{6}$/, message: 'Enter a valid 6-digit pincode' },
            ]}>
            <Input prefix={<EnvironmentOutlined />} placeholder="e.g. 600001" maxLength={6} size="large" />
          </Form.Item>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#2874f0', borderColor: '#2874f0', borderRadius: 4, fontWeight: 600 }}>
              {isEditing ? 'Update' : 'Add Pincode'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PincodeMaster;
